import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, OnDestroy, Renderer2, RendererFactory2 } from '@angular/core';
import { Router } from '@angular/router';
import { pushToGTM } from '../utilities/analytics';

type TrackedElement = HTMLElement & {
  dataset: DOMStringMap;
};

@Injectable({
  providedIn: 'root',
})
export class UiAnalyticsTrackerService implements OnDestroy {
  private readonly renderer: Renderer2;
  private removeClickListener?: () => void;
  private trackingStarted = false;

  constructor(
    rendererFactory: RendererFactory2,
    @Inject(DOCUMENT) private document: Document,
    private router: Router
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  startTracking(): void {
    if (this.trackingStarted) {
      return;
    }

    this.removeClickListener = this.renderer.listen(
      this.document,
      'click',
      (event: Event) => this.handleDocumentClick(event)
    );
    this.trackingStarted = true;
  }

  stopTracking(): void {
    if (this.removeClickListener) {
      this.removeClickListener();
      this.removeClickListener = undefined;
    }

    this.trackingStarted = false;
  }

  ngOnDestroy(): void {
    this.stopTracking();
  }

  private handleDocumentClick(event: Event): void {
    const clickableElement = this.getClickableElement(event);

    if (!clickableElement || clickableElement.dataset?.['analyticsIgnore'] === 'true') {
      return;
    }

    const buttonName = this.getButtonName(clickableElement);
    if (!buttonName) {
      return;
    }

    const routeUrl = this.router.url || this.document.location.pathname || '/';
    const moduleName = this.getModuleName(clickableElement, routeUrl);
    const componentName = this.getComponentName(clickableElement);
    const hostname = this.document.location.hostname || 'unknown-host';

    pushToGTM({
      category: 'click',
      action: `${moduleName} ${buttonName} clicked`,
      label: {
        feature: moduleName,
        subFeature: `${componentName} | ${buttonName}`,
        metaData: {
          hostname,
          moduleName,
          componentName,
          buttonName,
          routeUrl,
        },
      },
    });
  }

  private getClickableElement(event: Event): TrackedElement | null {
    const clickSelector =
      'button, a, [role="button"], [type="button"], [type="submit"], mc-button, [data-analytics-button]';

    const eventPath = typeof event.composedPath === 'function' ? event.composedPath() : [];
    for (const pathItem of eventPath) {
      if (pathItem instanceof HTMLElement) {
        const matchedElement = pathItem.matches(clickSelector)
          ? pathItem
          : pathItem.closest(clickSelector);

        if (matchedElement instanceof HTMLElement) {
          return matchedElement as TrackedElement;
        }
      }
    }

    const target = event.target;
    if (target instanceof HTMLElement) {
      const matchedElement = target.matches(clickSelector)
        ? target
        : target.closest(clickSelector);

      if (matchedElement instanceof HTMLElement) {
        return matchedElement as TrackedElement;
      }
    }

    return null;
  }

  private getButtonName(element: TrackedElement): string {
    const explicitName = element.dataset['analyticsButton']?.trim();
    if (explicitName) {
      return explicitName;
    }

    const attrName =
      element.getAttribute('label') ||
      element.getAttribute('aria-label') ||
      element.getAttribute('title') ||
      element.getAttribute('name') ||
      element.getAttribute('id');

    if (attrName?.trim()) {
      return attrName.trim();
    }

    const textContent = element.textContent?.replace(/\s+/g, ' ').trim();
    if (textContent) {
      return textContent;
    }

    return 'unnamed-button';
  }

  private getModuleName(element: TrackedElement, routeUrl: string): string {
    const explicitModule = element.dataset['analyticsModule']?.trim();
    if (explicitModule) {
      return explicitModule;
    }

    const cleanedUrl = routeUrl.split('?')[0].split('#').pop() || '';
    const segments = cleanedUrl.split('/').filter(Boolean);

    return segments[0] || 'root';
  }

  private getComponentName(element: TrackedElement): string {
    const explicitComponent = element.dataset['analyticsComponent']?.trim();
    if (explicitComponent) {
      return explicitComponent;
    }

    const routeComponentName = this.getActivatedRouteComponentName();
    if (routeComponentName) {
      return routeComponentName;
    }

    return 'unknown-component';
  }

  private getActivatedRouteComponentName(): string | null {
    let snapshot = this.router.routerState.snapshot.root;

    while (snapshot.firstChild) {
      snapshot = snapshot.firstChild;
    }

    const component = snapshot.routeConfig?.component as { name?: string } | undefined;

    return component?.name ?? null;
  }
}